import { useEffect, useState, type CSSProperties } from 'react'
import { ImageOff, LoaderCircle } from 'lucide-react'

/**
 * A freshly created link serves its manifest before every variant has been
 * written, so the first thumbnail requests can 404 / 423 for a few seconds.
 * Re-request a handful of times with backoff before calling the image broken.
 */
export const THUMB_RETRY_DELAYS_MS = [1500, 3000, 6000] as const

interface PhotoSurfaceProps {
  src: string
  alt?: string
  className?: string
  rounded?: string
  children?: React.ReactNode
  style?: CSSProperties
  /**
   * `cover` (default) makes the image fill the container and crop to
   * fit. `contain` fits the image entirely within the container with
   * letterboxing — used in the fullscreen photo view so the whole
   * frame is visible.
   */
  fit?: 'cover' | 'contain'
  /**
   * Override the default lazy-load behaviour. Set on hero / above-the-fold
   * images so they decode immediately instead of waiting for intersection.
   */
  eager?: boolean
  /**
   * Override the classes applied directly to the img tag.
   * Useful for masonry cells that need intrinsic width/height control.
   */
  imgClassName?: string
  /**
   * Reports the decoded image's intrinsic size on load. Used by the markup
   * overlay to compute the object-fit:contain letterbox so drawn regions land
   * on the exact visible pixels.
   */
  onImageLoad?: (size: { naturalWidth: number; naturalHeight: number }) => void
}

/**
 * Renders a real photo URL with progressive load, a soft shimmer
 * placeholder while loading, and an explicit "broken image" indicator
 * on decode failure. The container is always `position: relative` so
 * overlays (shimmer, error badge, caller-provided children) layer on
 * top reliably.
 */
export function PhotoSurface({
  src,
  alt,
  className = '',
  rounded = 'rounded-xl',
  children,
  style,
  fit = 'cover',
  eager = false,
  imgClassName = 'w-full h-full',
  onImageLoad,
}: PhotoSurfaceProps) {
  const [imageState, setImageState] = useState({
    src: '',
    loaded: false,
    errored: false,
    attempt: 0,
  })
  // Derive a fresh state synchronously when src changes. Resetting in an effect
  // races cached images: their load event can fire first, then the effect hides
  // the already-decoded pixels at opacity zero.
  const stateForSrc =
    imageState.src === src ? imageState : { src, loaded: false, errored: false, attempt: 0 }
  const { loaded, errored, attempt } = stateForSrc

  useEffect(() => {
    if (!errored || attempt >= THUMB_RETRY_DELAYS_MS.length) return
    const timer = window.setTimeout(() => {
      setImageState((current) => ({
        src,
        loaded: false,
        errored: false,
        attempt: current.src === src ? current.attempt + 1 : 1,
      }))
    }, THUMB_RETRY_DELAYS_MS[attempt])
    return () => window.clearTimeout(timer)
  }, [errored, attempt])

  const gaveUp = errored && attempt >= THUMB_RETRY_DELAYS_MS.length
  // `relative` is only the default. Callers that pass their own positioning
  // (the photo viewer and the Review tiles both pass `absolute inset-0`) used
  // to lose the race on specificity, so the surface collapsed to a static
  // block, `h-full` on the img resolved against an auto height, and the photo
  // rendered at natural aspect anchored to the top of its box instead of
  // filling it — 380px of dead space under the photo on a 390x844 phone.
  const positioned = /(^|\s)(absolute|fixed|sticky)(\s|$)/.test(className)
  return (
    <div
      className={`${positioned ? '' : 'relative'} overflow-hidden ${rounded} ${className}`}
      style={{
        background: gaveUp
          ? 'linear-gradient(160deg, var(--surface-secondary), var(--surface-tertiary))'
          : 'var(--surface-secondary)',
        ...style,
      }}
      data-testid="photo-surface"
      data-attempt={attempt}
      data-loaded={loaded ? 'true' : 'false'}
    >
      {!errored && (
        <img
          key={attempt}
          src={src}
          alt={alt ?? ''}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={(e) => {
            setImageState({ src, loaded: true, errored: false, attempt })
            const img = e.currentTarget
            onImageLoad?.({ naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight })
          }}
          onError={() => setImageState({ src, loaded: false, errored: true, attempt })}
          className={`${imgClassName} transition-opacity duration-300`}
          style={{
            // Keep the fit contract independent of utility-CSS availability.
            // A stale/missing width or height utility otherwise lets a portrait
            // asset render at its intrinsic size and get clipped by the
            // overflow-hidden viewer even though objectFit says "contain".
            display: 'block',
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: fit,
            objectPosition: 'center center',
            opacity: loaded ? 1 : 0,
          }}
          draggable={false}
        />
      )}
      {!loaded && !gaveUp && (
        <div
          role="status"
          aria-live="polite"
          data-testid="photo-surface-skeleton"
          className="absolute inset-0 flex items-center justify-center"
          style={{
            background:
              'linear-gradient(110deg, var(--surface-secondary) 25%, var(--surface-tertiary) 50%, var(--surface-secondary) 75%)',
            backgroundSize: '200% 100%',
          }}
        >
          <span className="flex items-center gap-2 rounded-full border border-white/10 bg-black/55 px-3 py-1.5 text-[11px] font-medium text-white/75 shadow-sm backdrop-blur-sm">
            <LoaderCircle className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden />
            Loading photo
          </span>
        </div>
      )}
      {gaveUp && (
        <div
          className="absolute inset-0 grid place-items-center text-[var(--text-tertiary)]"
          role="img"
          aria-label={alt ? `Image failed to load: ${alt}` : 'Image failed to load'}
        >
          <div className="flex flex-col items-center gap-1.5 px-3 text-center">
            <ImageOff size={20} strokeWidth={1.6} aria-hidden />
            {alt && (
              <span className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] line-clamp-2">
                {alt}
              </span>
            )}
          </div>
        </div>
      )}
      {children}
    </div>
  )
}
